import styled from "styled-components";

import { formatAdminDate, formatAdminDateRange } from "@pages/AdminDashboard/formatAdminDate";
import { InvoiceLink } from "@pages/AdminDashboard/InvoiceLink";
import { OpenInButtons } from "@pages/AdminDashboard/OpenInButtons";
import { telehealthCaseUrl } from "@pages/AdminDashboard/telehealthLinks";
import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { Card } from "@pages/AdminDashboard/ui/Card";
import { DataTable, DataTableColumn, EmptyStateCard } from "@pages/AdminDashboard/ui/DataTable";
import { ExternalLink, shortenIdentifier } from "@pages/AdminDashboard/ui/DataTable/cells";
import { StatusBadge } from "@pages/AdminDashboard/ui/StatusBadge";
import { Case, Trip } from "@types-runway/case";

import { describeCaseOrders } from "./describeCaseOrders";

const { color, typeScale } = adminTokens;
const [, spaceSm] = adminTokens.spacing;

const Hint = styled.span`
  color: ${color.inkSoft};
  font-family: ${typeScale.meta.fontFamily};
  font-size: ${typeScale.meta.fontSize};
`;

const CaseCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${spaceSm}px 0;
`;

const OrdersSummary = styled.span`
  color: ${color.inkSoft};
`;

const CaseNumber = styled.span`
  font-family: ${typeScale.labelValue.fontFamily};
`;

const noValue = "—";
const noTripDate = "No trip date";

// CVIEW-R4: the destination, a dot and the dates of the trip.
const describeTrip = (trip?: Trip & { destination?: string }) => {
  if (!trip?.from) return noTripDate;

  const dates = formatAdminDateRange(trip.from, trip.to);

  return trip.destination ? `${trip.destination} · ${dates}` : dates;
};

const columns: DataTableColumn<Case>[] = [
  {
    id: "case",
    header: "Case",
    render: ({ id, caseId }) => (
      <CaseCell>
        <CaseNumber>{id}</CaseNumber>
        {caseId ? (
          <ExternalLink href={telehealthCaseUrl(caseId)} title={caseId}>
            {shortenIdentifier(caseId)}
          </ExternalLink>
        ) : (
          noValue
        )}
      </CaseCell>
    ),
  },
  {
    id: "status",
    header: "Status",
    phonePlacement: "badge",
    render: (caseRecord) => <StatusBadge status={caseRecord.case_status ?? noValue} />,
  },
  {
    id: "paid",
    header: "Paid",
    render: ({ paid, invoiceId }) => (
      <InvoiceLink invoiceId={invoiceId}>{paid ? "✓ Paid" : "Unpaid"}</InvoiceLink>
    ),
  },
  { id: "trip", header: "Trip", render: ({ trip }) => describeTrip(trip) },
  {
    id: "orders",
    header: "Orders",
    render: ({ orders }) => <OrdersSummary>{describeCaseOrders(orders)}</OrdersSummary>,
  },
  {
    id: "openIn",
    header: "Open in",
    align: "end",
    render: ({ caseId }) => <OpenInButtons caseId={caseId} />,
  },
];

// On a phone the Open in Buttons sit at the bottom of each card with no header (FIND-R22).
const phoneColumns = columns.map((column) =>
  column.id === "openIn" ? { ...column, isHeaderHidden: true } : column
);

interface UserCasesProps {
  cases: Case[];
}

// FIND-E5: a user result lists their cases; the columns and cells are those of CVIEW-R1…R7, and
// the Orders column joins them with the Orders on each Case (ENG-843). USER-R20.
const UserCases = ({ cases }: UserCasesProps) => {
  const isPhone = useIsPhoneLayout();

  return (
    <div data-testid="user-cases">
      <Card
        title={`Cases · ${cases.length}`}
        titleLevel={3}
        aside={<Hint>Open a Case to cancel, replace or change shipping</Hint>}
      >
        {cases.length === 0 ? (
          <EmptyStateCard title="No cases on file." />
        ) : (
          <DataTable
            label="Cases"
            columns={isPhone ? phoneColumns : columns}
            rows={cases}
            getRowKey={(caseRecord) => String(caseRecord.id)}
            emptyState={{ title: "No cases on file." }}
          />
        )}
      </Card>
    </div>
  );
};

export default UserCases;
